import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button, Flex } from '@maxhub/max-ui';
import { Event } from '../types';

interface CalendarViewProps {
  isOpen: boolean;
  events: Event[];
  onClose: () => void;
  onSelectEvent: (eventId: number) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  isOpen,
  events,
  onClose,
  onSelectEvent,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const minSwipeDistance = 50;

  // Группируем мероприятия по дням текущего месяца
  const eventsByDay = useMemo(() => {
    const map: Record<number, Event[]> = {};
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    events.forEach(event => {
      const eventDate = new Date(event.datetime);
      if (eventDate.getFullYear() === year && eventDate.getMonth() === month) {
        const day = eventDate.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(event);
      }
    });

    // Сортировка по времени
    Object.keys(map).forEach(day => {
      map[Number(day)].sort((a, b) =>
        new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
      );
    });

    return map;
  }, [events, currentDate]);

  // Сетка календаря
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();

    const days: Array<{ day: number | null; date: Date | null }> = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ day, date: new Date(year, month, day) });
    }

    return days;
  }, [currentDate]);

  const monthYearLabel = currentDate.toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
  });

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) goToNextMonth();
    if (isRightSwipe) goToPreviousMonth();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = (date: Date | null) => {
    if (!date) return false;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  };

  const isPastEvent = (event: Event) => {
    return new Date(event.datetime) < new Date();
  };

  const formatEventTime = (datetime: string) => {
    return new Date(datetime).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          maxWidth: 500,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <Flex style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button
            onClick={goToPreviousMonth}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              padding: '4px 12px',
              color: '#007AFF',
            }}
          >
            ←
          </button>
          <h3 style={{ margin: 0, fontSize: 18, textTransform: 'capitalize' }}>
            {monthYearLabel}
          </h3>
          <button
            onClick={goToNextMonth}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              padding: '4px 12px',
              color: '#007AFF',
            }}
          >
            →
          </button>
        </Flex>

        {/* Дни недели */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 4,
          marginBottom: 8,
        }}>
          {weekDays.map(day => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                color: '#999',
                padding: 4,
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Сетка календаря */}
        <div
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4,
          }}
        >
          {calendarDays.map((cell, index) => {
            if (cell.day === null) {
              return <div key={index} style={{ minHeight: 80 }} />;
            }

            const dayEvents = eventsByDay[cell.day] || [];
            const hasEvents = dayEvents.length > 0;
            const todayMark = isToday(cell.date);

            return (
              <div
                key={index}
                style={{
                  minHeight: 80,
                  padding: 4,
                  borderRadius: 8,
                  backgroundColor: todayMark ? '#e3f2fd' : (hasEvents ? '#f5f5f5' : 'transparent'),
                  border: todayMark ? '2px solid #007AFF' : '1px solid #eee',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <div style={{
                  fontSize: 14,
                  fontWeight: todayMark ? 'bold' : 'normal',
                  color: todayMark ? '#007AFF' : '#333',
                  marginBottom: 2,
                }}>
                  {cell.day}
                </div>

                {dayEvents.slice(0, 3).map(event => {
                  const isPast = isPastEvent(event);
                  const time = formatEventTime(event.datetime);

                  // Обрезаем название до 8 символов
                  const shortTitle =
                    event.title.length > 8
                      ? event.title.substring(0, 8) + '…'
                      : event.title;

                  return (
                    <div
                      key={event.id}
                      onClick={() => {
                        onSelectEvent(event.id);
                        onClose();
                      }}
                      style={{
                        fontSize: 9,
                        lineHeight: 1.1,
                        padding: '2px 3px',
                        borderRadius: 3,
                        backgroundColor: isPast ? 'rgba(76, 175, 80, 0.4)' : '#4CAF50',
                        color: '#fff',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        opacity: isPast ? 0.6 : 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                      }}
                      title={`${time} — ${event.title}${isPast ? ' (прошло)' : ''}`}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: 9 }}>{time}</div>
                      <div
                        style={{
                          fontSize: 8,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        {shortTitle}
                      </div>
                    </div>
                  );
                })}

                {dayEvents.length > 3 && (
                  <div style={{ fontSize: 9, color: '#666', textAlign: 'center' }}>
                    +{dayEvents.length - 3}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Легенда */}
        <div style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'center',
          marginTop: 12,
          fontSize: 11,
          color: '#666',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 3,
              backgroundColor: '#4CAF50',
            }} />
            Предстоящие
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 3,
              backgroundColor: 'rgba(76, 175, 80, 0.4)',
            }} />
            Прошедшие
          </div>
        </div>

        {/* Кнопка закрытия */}
        <Flex style={{ justifyContent: 'center', marginTop: 16 }}>
          <Button
            onClick={onClose}
            style={{
              padding: '8px 24px',
              backgroundColor: '#EBC8C4',
              color: '#333',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Закрыть
          </Button>
        </Flex>
      </div>
    </div>
  );
};